// Planni suggestion rules engine. Suggestions are derived from the user's actual data/state,
// never random. Each rule returns a suggestion { id, priority, category, title, body, action } or null.
// Extensible: add a rule function and it is picked up automatically. Higher priority shows first.

const daysUntil = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.floor(ms / 86400000);
};

// ctx: { data } where data is the dashboard/home snapshot we fetch once. Rules read defensively.
const RULES = [
  // Feature discovery - Secure Share
  (ctx) => ({
    id: "discover-secure-share",
    priority: 20,
    category: "discovery",
    title: "Share important things safely",
    body: "Did you know you can securely share documents and records with family, with expiry dates and the ability to revoke access at any time?",
    action: { label: "Explore Secure Share", to: "/app/secure-share" },
  }),
  // MOT expiring soon (rule-based, from the user's recorded MOT date). A calm, actionable nudge.
  (ctx) => {
    const d = daysUntil(ctx.data?.soonest_mot_expiry);
    if (d !== null && d <= 30 && d >= 0) {
      const veh = ctx.data?.mot_vehicle_name ? ` for ${ctx.data.mot_vehicle_name}` : "";
      return {
        id: `mot-expiring-${ctx.data?.soonest_mot_expiry}`,
        priority: 85,
        category: "vehicle",
        title: "An MOT is due soon",
        body: `Your MOT${veh} is due in about ${d} day${d === 1 ? "" : "s"}. It's worth booking it in to avoid driving without a valid MOT.`,
        action: { label: "Open Vehicle Hub", to: "/app/vehicle-health" },
      };
    }
    return null;
  },
  // Home insurance missing
  (ctx) => {
    if (ctx.data?.home_insurance_missing) {
      return {
        id: "home-insurance-missing",
        priority: 80,
        category: "home",
        title: "Add your home insurance",
        body: "You haven't recorded your home insurance yet. Add it now and I'll remind you before it renews.",
        action: { label: "Add to Home Hub", to: "/app/responsibilities" },
      };
    }
    return null;
  },
  // Passport expiring
  (ctx) => {
    const d = daysUntil(ctx.data?.soonest_passport_expiry);
    if (d !== null && d <= 180 && d >= 0) {
      return {
        id: "passport-expiring",
        priority: 90,
        category: "travel",
        title: "A passport is expiring soon",
        body: `A passport in your household expires in about ${d} day${d === 1 ? "" : "s"}. Many countries require six months' validity, so it may be worth renewing.`,
        action: { label: "Open Travel Hub", to: "/app/travel" },
      };
    }
    return null;
  },
  // Positive reinforcement - earned, sparing, never gamified. Low priority so attention items win.
  (ctx) => {
    if (ctx.data?.pet_reminders_ok) {
      return {
        id: "positive-pet-reminders",
        priority: 12,
        category: "positive",
        title: "Your pets are up to date",
        body: "All of your pet reminders are configured. That is exactly the kind of thing that saves worry later.",
        action: null,
      };
    }
    return null;
  },
  (ctx) => {
    if (ctx.data?.all_caught_up && ctx.data?.has_documents) {
      return {
        id: "positive-organised",
        priority: 11,
        category: "positive",
        title: "Your household is well organised",
        body: "Everything looks up to date and your important documents are safely stored. Lovely work keeping on top of things.",
        action: null,
      };
    }
    return null;
  },
  // Positive reinforcement - everything up to date (earned only when reminders actually exist)
  (ctx) => {
    if (ctx.data?.all_caught_up && ctx.data?.has_reminders) {
      return {
        id: "all-caught-up",
        priority: 10,
        category: "positive",
        title: "You're all caught up",
        body: "Everything looks up to date across your household. Lovely work keeping things in order.",
        action: null,
      };
    }
    return null;
  },
  // ---- Household Health (authoritative score; Planni never invents a separate one) ----
  // A category has dipped and there is a concrete next action to take. Highest of the health
  // rules so genuine attention wins over celebration.
  (ctx) => {
    const h = ctx.data?.health;
    const rec = h?.top_recommendation;
    if (h && typeof h.overall === "number" && h.overall < 75 && rec?.text) {
      return {
        id: "health-attention",
        priority: 70,
        category: "health",
        title: "One thing worth a look",
        body: `Your Household Health Score is ${h.overall}. A small step would help: ${rec.text}.`,
        // Contextual: take the user straight to the thing, not a generic dashboard.
        action: rec.route ? { label: "Take me there", to: rec.route } : { label: "Open Health Score", to: "/app/health" },
      };
    }
    return null;
  },
  // Significant improvement since Planni last checked. Warm, earned acknowledgement.
  (ctx) => {
    const h = ctx.data?.health;
    const prev = ctx.data?.health_prev;
    if (h && typeof h.overall === "number" && typeof prev === "number" && h.overall - prev >= 8) {
      return {
        id: "health-improved",
        priority: 30,
        category: "positive",
        title: "Lovely progress",
        body: `Your Household Health Score has climbed to ${h.overall}. That is exactly the kind of quiet, steady care that pays off later.`,
        action: null,
      };
    }
    return null;
  },
  // Excellent, fully protected household. Sparing, low priority so attention items always win.
  (ctx) => {
    const h = ctx.data?.health;
    if (h && typeof h.overall === "number" && h.overall >= 90) {
      return {
        id: "health-excellent",
        priority: 14,
        category: "positive",
        title: "Your household is fully protected",
        body: `Your Household Health Score is ${h.overall}. Everything important is in place and up to date. Lovely work.`,
        action: null,
      };
    }
    return null;
  },
  // ---- Travel: a calm nudge as departure approaches. Per-trip id means it shows at most once per
  // trip (dismissal/snooze persists), and the 1-14 day window keeps it from appearing too early. ----
  (ctx) => {
    const t = ctx.data?.next_trip;
    const d = t?.days_to_departure;
    if (!t?.trip_id || typeof d !== "number" || d < 1 || d > 14) return null;
    const where = t.destination ? ` to ${t.destination}` : "";
    let title, body;
    if (d <= 3) {
      title = "Your trip is almost here";
      body = `You're ${d} ${d === 1 ? "day" : "days"} from departure${where}. It's worth checking your travel plans are ready.`;
    } else if (d <= 7) {
      title = "Check-in may be opening soon";
      body = `Your trip${where} is ${d} days away. It's worth reviewing your airport and passport details.`;
    } else {
      title = "Your trip is coming up soon";
      body = `You have a trip${where} in ${d} days. Would you like to check your travel documents?`;
    }
    return {
      id: `travel-departure-${t.trip_id}`,
      // An imminent departure (<= 7 days) is time-critical, so it outranks the general health nudge;
      // a more distant trip (8-14 days) defers to household health.
      priority: d <= 7 ? 78 : 52,
      category: "travel",
      title,
      body,
      action: { label: "Check travel documents", to: `/app/travel/${t.trip_id}?check=documents` },
    };
  },
];

export function buildSuggestions(ctx) {
  const out = [];
  for (const rule of RULES) {
    try {
      const s = rule(ctx || {});
      if (s) out.push(s);
    } catch { /* a broken rule must never break Planni */ }
  }
  return out.sort((a, b) => b.priority - a.priority);
}
